import React, { useState, useEffect, useCallback } from 'react';
import PageWrapper from '../../../pagecomponents/Common/PageWrapper';
import { Row, InputGroup, Button, Form, Table } from 'react-bootstrap';
import { CCol, CFormLabel } from '@coreui/react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';

import { getDayBook } from '../../../redux/slices/DayBookSlice';
import { getMasterData } from '../../../redux/slices/MasterClientSlice';
import { toastMessage } from '../../../helpers/utility';
import { checkEmpty, number_format } from '../../../../src/helpers/utility';
import NoData from '../../../pagecomponents/Common/NoData';

import './daybook.css';

const INIT_FORM_DATA = {
  fromdate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // yyyy-MM-dd
  todate: new Date().toISOString().split('T')[0], // yyyy-MM-dd
  payment_mode: '',
  voucher_type: '',
  sorting: 'asc',
  sorting_key: 'voucher_Date',
};

const toStartOfDay = (date) => `${format(new Date(date), 'yyyy-MM-dd')} 00:00:00`;
const toEndOfDay = (date) => `${format(new Date(date), 'yyyy-MM-dd')} 23:59:59`;

const DayBook = () => {
  const pageName = 'Day Book';
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [searchedData, setSearchedData] = useState([]);
  const [paymentMode, setPaymentMode] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  const masterListData = useCallback(async () => {
    const { payload } = await dispatch(getMasterData({ inputData: {} }));
    if (payload?.status === 'success') {
      setPaymentMode(payload?.payment_mode || []);
    }
  }, [dispatch]);

  const searchDayBook = useCallback(async () => {
    const { payload } = await dispatch(getDayBook({ inputData: formData }));
    if (payload?.status === 'error') {
      toastMessage('error', payload?.msg || 'An error occurred while fetching day book.');
      return;
    }

    setSearchedData(payload?.data || []);
    setSummaryData(payload?.summary || null);
  }, [dispatch, formData]);

  useEffect(() => {
    (async () => {
      await masterListData();
      await searchDayBook();
    })();
  }, [masterListData, searchDayBook]);

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, [name]: '' }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === 'fromdate' ? toStartOfDay(date) : toEndOfDay(date),
    }));
  };

  const handleSort = (key) => {
    setFormData((prev) => ({
      ...prev,
      sorting_key: key,
      sorting: prev.sorting === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortingIcon = (key) => {
    if (formData.sorting_key === key) {
      return formData.sorting === 'asc' ? <i className="fa fa-caret-up" /> : <i className="fa fa-caret-down" />;
    }
    return <i className="fas fa-exchange-alt daybook-sort-icon" />;
  };

  const renderVoucherType = (t) => {
    if (t === 'Customer') return 'Receipt Voucher';
    if (t === 'Supplier') return 'Payment Voucher';
    return 'Journal Voucher';
  };

  return (
    <PageWrapper pageName={pageName}>
      <div className="daybook-root">
        {/* Filters + actions (PRINT SHOULD SHOW THIS TOO) */}
        <Row className="row-xs mb-3 daybook-filters">
          <CCol md={3}>
            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="fromdate">
              From Date
            </CFormLabel>
            <InputGroup className="input-group reactdate-pic">
              <InputGroup.Text className="input-group-text">
                <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6" />
              </InputGroup.Text>
              <DatePicker
                className="form-control"
                name="fromdate"
                id="fromdate"
                selected={formData.fromdate ? new Date(formData.fromdate) : null}
                onChange={(date) => handleDateChange('fromdate', date)}
                dateFormat="yyyy-MM-dd"
              />
            </InputGroup>
          </CCol>

          <CCol md={3}>
            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="todate">
              To Date
            </CFormLabel>
            <InputGroup className="input-group reactdate-pic">
              <InputGroup.Text className="input-group-text">
                <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6" />
              </InputGroup.Text>
              <DatePicker
                className="form-control"
                name="todate"
                id="todate"
                selected={formData.todate ? new Date(formData.todate) : null}
                onChange={(date) => handleDateChange('todate', date)}
                dateFormat="yyyy-MM-dd"
              />
            </InputGroup>
          </CCol>

          {/* If you want Mode dropdown, uncomment */}
          {/* <CCol md={2}>
            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="payment_mode">
              Mode
            </CFormLabel>
            <Form.Select
              className="form-control"
              id="payment_mode"
              name="payment_mode"
              value={formData.payment_mode}
              onChange={handleSelectChange}
            >
              <option value="">All</option>
              {paymentMode?.map((option) => (
                <option key={option.payment_mode_master_Id} value={option.payment_mode_master_Id}>
                  {option.payment_mode_Name}
                </option>
              ))}
            </Form.Select>
          </CCol> */}

          <CCol md={2}>
            <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="voucher_type">
              Vch Type
            </CFormLabel>
            <Form.Select
              className="form-control"
              id="voucher_type"
              name="voucher_type"
              value={formData.voucher_type}
              onChange={handleSelectChange}
            >
              <option value="">All</option>
              <option value="Payment">Payment Voucher</option>
              <option value="Receipt">Receipt Voucher</option>
              <option value="Expense">Journal Voucher</option>
            </Form.Select>
          </CCol>

          <CCol md={2} className="daybook-actions">
            <div className="daybook-actions-inner">
              <Button variant="" type="button" className="btn btn-sm btn-primary" onClick={searchDayBook}>
                Get Data
              </Button>

              <Button variant="" type="button" className="btn btn-sm btn-secondary" onClick={() => window.print()}>
                Print
              </Button>
            </div>
          </CCol>
        </Row>

        {/* Summary */}
        {!!summaryData && (
          <Row className="row-sm mb-2 daybook-summary-row">
            <CCol md={4} className="ms-auto">
              <Table className="table table-invoice table-bordered daybook-summary">
                <thead>
                  <tr>
                    <th colSpan={2} className="bg-dark text-white tx-left">
                      Summary
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Debit</td>
                    <td className="tx-right">{number_format(summaryData.debitAmt, 2)}</td>
                  </tr>
                  <tr>
                    <td>Credit</td>
                    <td className="tx-right">{number_format(summaryData.creditAmt, 2)}</td>
                  </tr>
                </tbody>
                <tfoot className="border-bottom bg-dark text-white">
                  <tr>
                    <th>Closing Balance</th>
                    <th className="tx-right">{number_format(summaryData.closing_balance, 2)}</th>
                  </tr>
                </tfoot>
              </Table>
            </CCol>
          </Row>
        )}

        {/* Data */}
        {!checkEmpty(searchedData) ? (
          <Table className="table table-bordered mb-0 daybook-table">
            <thead>
              <tr>
                <th className="text-center cursor-pointer" onClick={() => handleSort('voucher_Date')}>
                  Date <span>{getSortingIcon('voucher_Date')}</span>
                </th>
                <th className="text-center cursor-pointer" onClick={() => handleSort('voucher_No')}>
                  Vch No <span>{getSortingIcon('voucher_No')}</span>
                </th>
                <th className="text-left cursor-pointer" onClick={() => handleSort('voucher_payment_Name')}>
                  Particulars <span>{getSortingIcon('voucher_payment_Name')}</span>
                </th>
                <th className="text-center cursor-pointer" onClick={() => handleSort('voucher_payment_Type')}>
                  Vch Type <span>{getSortingIcon('voucher_payment_Type')}</span>
                </th>
                <th className="text-right">Debit Amount</th>
                <th className="text-right">Credit Amount</th>
              </tr>
            </thead>

            <tbody>
              {searchedData?.map((item, index) => {
                const isCredit = item?.ledger_type === 'Cr';
                const amt = number_format(item?.voucher_payment_Amount, 2);

                return (
                  <tr key={index}>
                    <td className="text-center">{String(item?.voucher_Date || '').split(' ')[0]}</td>
                    <td className="text-center">{item?.voucher_No}</td>
                    <td className="text-left">{item?.voucher_payment_Name}</td>
                    <td className="text-center">{renderVoucherType(item?.voucher_payment_Type)}</td>

                    <td className="text-right">{isCredit ? '' : amt}</td>
                    <td className="text-right">{isCredit ? amt : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <NoData />
        )}
      </div>
    </PageWrapper>
  );
};

export default DayBook;
